import { useEffect, useState } from "react";
import type { Column } from "../components/DataTable";
import type { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useBusy } from "../../context/BusyContext";
import AdminDataModal from "../components/AdminDataModal";
import DataTable from "../components/DataTable";
import AdminListToolkit from "../components/listing/AdminListToolkit";

export default function ProductTypes() {
  const BASE_URL = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { runBusy } = useBusy();

  const [data, setData] = useState<any[]>([]);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit" | "view">("add");
  const [activeRow, setActiveRow] = useState<any>(null);

  /* =========================
     FETCH TYPES
  ========================= */

  const fetchTypes = async () => {
    try {
      const res = await runBusy(() =>
        fetch(`${BASE_URL}/admin/types`, {
          credentials: "include",
        }),
      );

      const result = await res.json();
      const freshData = result.data || [];

      if (result.success) {
        setData(freshData);

        setSelectedRow((prev: any) =>
          prev
            ? freshData.find((item: any) => item.id === prev.id) || null
            : null,
        );
      }
    } catch (error) {
      console.error("Fetch Types Error:", error);
    }
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  /* =========================
     ADD / UPDATE
  ========================= */
  const handleSubmit = async (formData: any) => {
    try {
      if (!String(formData.description || "").trim()) {
        showModal({
          type: "error",
          title: "Description Required",
          message: "Please enter the Product Type description.",
          confirmText: "OK",
        });
        return;
      }

      if (!["online", "offline"].includes(formData.selling_market)) {
        showModal({
          type: "error",
          title: "Selling Market Required",
          message: "Please select Online or Offline as the Selling Market.",
          confirmText: "OK",
        });
        return;
      }

      const method = modalMode === "add" ? "POST" : "PUT";

      const url =
        modalMode === "add"
          ? `${BASE_URL}/admin/types`
          : `${BASE_URL}/admin/types/${activeRow.id}`;

      const res = await runBusy(() =>
        fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            description: String(formData.description).trim(),
            selling_market: formData.selling_market,
            status: formData.status || "active",
          }),
        }),
      );

      const result = await res.json();

      if (!res.ok || result.success === false) {
        showModal({
          type: "error",
          title: "Error",
          message:
            result.message ||
            (modalMode === "add"
              ? "Failed to add Product Type."
              : "Failed to update Product Type."),
          confirmText: "OK",
        });
        return;
      }

      await fetchTypes();

      setModalOpen(false);
      setActiveRow(null);
      setSelectedRow(null);

      showModal({
        type: "success",
        title: "Success",
        message:
          result.message ||
          (modalMode === "add"
            ? `Product type "${formData.description}" added successfully.`
            : `Product type "${formData.description}" updated successfully.`),
        confirmText: "OK",
      });
    } catch (error: any) {
      console.error("Product Type Submit Error:", error);

      showModal({
        type: "error",
        title: "Error",
        message:
          error?.message ||
          (modalMode === "add"
            ? "Failed to add Product Type."
            : "Failed to update Product Type."),
        confirmText: "OK",
      });
    }
  };

  /* =========================
     DELETE
  ========================= */
  const handleDelete = (row: any) => {
    showModal({
      type: "confirm",
      title: "Delete Product Type",
      message: `Are you sure you want to delete "${row.description}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: async () => {
        try {
          const res = await fetch(`${BASE_URL}/admin/types/${row.id}`, {
            method: "DELETE",
            credentials: "include",
          });

          const result = await res.json();

          if (result.success) {
            fetchTypes();
            setSelectedRow(null);

            showModal({
              type: "success",
              title: "Deleted",
              message: "Product type deleted successfully.",
              confirmText: "OK",
            });
          }
        } catch (error) {
          console.error("Delete Error:", error);
        }
      },
    });
  };

  /* =========================
     FIELDS
  ========================= */
  const typeFields: FormField[] = [
    {
      key: "id",
      label: "ID",
      colSpan: 2,
    },
    {
      key: "description",
      label: "Description",
      colSpan: 2,
    },
    {
      key: "selling_market",
      label: "Selling Market",
      type: "select",
      options: [
        { label: "Online", value: "online" },
        { label: "Offline", value: "offline" },
      ],
      colSpan: 2,
    },
    {
      key: "status",
      label: "Status",
      type: "select",
      options: [
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
      ],
      colSpan: 2,
    },
  ];

  /* =========================
     COLUMNS
  ========================= */
  const columns: Column[] = [
    {
      key: "id",
      label: "ID",
    },
    {
      key: "description",
      label: "Description",
    },
    {
      key: "selling_market",
      label: "Selling Market",
      render: (row) => {
        const market = String(row.selling_market || "online").toLowerCase();

        return (
          <span
            className={market === "online" ? "badge-success" : "badge-warning"}
          >
            {market === "online" ? "Online" : "Offline"}
          </span>
        );
      },
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span
          className={row.status === "active" ? "badge-success" : "badge-danger"}
        >
          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1)}
        </span>
      ),
    },
  ];

  return (
    <div>
      <AdminListToolkit
        title="Product Types"
        columns={columns}
        data={data}
        selectedRow={selectedRow}
        onAdd={() => {
          setModalMode("add");

          setActiveRow({
            id: "",
            description: "",
            selling_market: "online",
            status: "active",
          });

          setModalOpen(true);
        }}
        onView={(row) => {
          setModalMode("view");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onEdit={(row) => {
          setModalMode("edit");
          setActiveRow(row);
          setModalOpen(true);
        }}
        onSoftDelete={handleDelete}
        enableExport={true}
        exportFileName="product_types"
      >
        {(filteredData) => (
          <DataTable
            columns={columns}
            data={filteredData}
            selectedRowId={selectedRow?.id}
            onRowSelect={setSelectedRow}
          />
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Product Type"
        fields={typeFields}
        rowData={activeRow}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
